import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { usePublicData } from "../../context/PublicDataContext";
import { getIcon } from "../../lib/icons";

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};
const item = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

export const Services = () => {
  const { services } = usePublicData();
  return (
    <section
      id="services"
      className="relative py-24 md:py-32"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <motion.p
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="text-sm font-semibold text-[#0033FF] uppercase tracking-widest mb-3"
            >
              What we deliver
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-display font-extrabold tracking-tighter text-4xl md:text-5xl text-[#0b1221] max-w-2xl"
            >
              One app for everything your city offers.
            </motion.h2>
          </div>
          <p className="text-[#5e6c84] max-w-sm">
            From daily essentials to last-minute surprises — pick a category and
            we handle the rest.
          </p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((s, idx) => {
            const Icon = getIcon(s.iconName);
            return (
              <motion.div
                key={s.id}
                variants={item}
                className={`group relative rounded-2xl p-8 border transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1.5 ${
                  s.comingSoon
                    ? "bg-[#0b1221] text-white border-transparent"
                    : "bg-white border-[#0033FF]/8 hover:shadow-[0_20px_50px_rgba(0,51,255,0.12)] hover:border-[#0033FF]/25"
                }`}
                data-testid={`service-card-${s.id}`}
              >
                <div className="flex items-start justify-between mb-8">
                  {s.image ? (
                    <span className="w-16 h-16 rounded-2xl overflow-hidden ring-1 ring-[#0033FF]/10 shadow-sm transition-transform duration-300 group-hover:scale-110">
                      <img
                        src={s.image}
                        alt={s.title}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover"
                      />
                    </span>
                  ) : (
                    <span
                      className={`w-16 h-16 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 ${
                        s.comingSoon
                          ? "bg-white/10 text-white"
                          : "bg-[#0033FF]/8 text-[#0033FF]"
                      }`}
                    >
                      <Icon className="w-7 h-7" />
                    </span>
                  )}
                  <span
                    className={`font-display font-bold text-sm ${
                      s.comingSoon ? "text-white/40" : "text-[#0033FF]/30"
                    }`}
                  >
                    0{idx + 1}
                  </span>
                </div>
                <h3 className="font-display font-bold text-2xl mb-3 flex items-center gap-2">
                  {s.title}
                  {s.comingSoon && (
                    <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#0033FF] text-white px-2 py-0.5 rounded-full">
                      Coming Soon
                    </span>
                  )}
                </h3>
                <p
                  className={`text-sm leading-relaxed ${
                    s.comingSoon ? "text-white/60" : "text-[#5e6c84]"
                  }`}
                >
                  {s.desc}
                </p>
                {!s.comingSoon && (
                  <ArrowUpRight className="absolute top-8 right-8 w-5 h-5 text-[#0033FF] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
